import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { useOnClickOutside } from 'lib/utils';
import { Dropdown, Modal, PrimaryButton, ButtonSwitch } from 'lib/components';
import CloseIcon from 'lib/images/CloseIcon';
import { theme } from 'lib/style';
import {
  addRecurring,
  editRecurring,
  useCustomReportsContext,
  useAuth,
} from 'lib/context';
import { RecipientsSelector } from './RecipientsSelector';
import {
  hoursOptions,
  isTimeValid,
  minutesOptions,
  timeFormats,
  timezonesOptions,
} from '../util';
import { Gap } from 'lib/components/styles/layout';
import { FrequencySelector } from 'lib/components/frequency/FrequencySelector';

const Header = styled.div`
  display: flex;
`;

const HeaderTitle = styled.div`
  ${theme.fontBold700}
  font-size: ${theme.fontSizes.lg};
  color: ${theme.palette.themeDark};
`;

const CloseButtonWrap = styled.div`
  margin-left: auto;
  color: ${theme.palette.white};
  height: 24px;
  width: 24px;
  &:hover {
    opacity: 0.7;
    cursor: pointer;
  }
`;

const Content = styled.div`
  width: 800px;
  height: 400px;
  display: flex;
  flex-direction: column;
  margin-bottom: 100px;
`;

const Label = styled.div`
  font-size: 14px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.43;
  letter-spacing: normal;
  color: #9297a2;
  margin-bottom: 8px;
`;

const DropdownContainer = styled.div`
  width: 200px;
  margin-left: 12px;
  & > .recurringDropdown > div {
    border-radius: 4px;
    > div {
      flex-wrap: nowrap;
    }
  }
  & > .recurringMonthlyDropdown {
    width: 200px;
    margin: 0 10px;
  }
  & > .recurringMonthlyDropdown > div {
    border-radius: 4px;
  }
`;

const TimeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  > svg {
    position: absolute;
    margin: 12px 8px;
  }
`;

const ErrorMessage = styled.div`
  height: 20px;
  font-size: 14px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.43;
  letter-spacing: normal;
  color: #e84c3d;
  padding-top: 4px;
`;

type Props = {
  reportId: string;
  handleModalClose: (recurringId?: string) => void;
  recurring?: any;
  createNew?: boolean;
  setCustomReport?: (id: string) => void;
};

export const RecurringModal = (props: Props) => {
  const [recipients, setRecipients] = useState([] as string[]);
  const [recipientsError, setRecipientsError] = React.useState('');
  const [frequencyString, setFrequencyString] = React.useState<
    string | undefined
  >(props.recurring?.frequency);
  const [timezone, setTimezone] = React.useState(timezonesOptions[0]);
  const [timeFormat, setTimeFormat] = React.useState('AM');
  const [loading, setLoading] = React.useState(false);
  const [hours, setHours] = React.useState(hoursOptions[0]);
  const [minutes, setMinutes] = React.useState(minutesOptions[0]);

  const { dispatch } = useCustomReportsContext();
  const {
    handleModalClose,
    reportId,
    recurring,
    createNew = true,
    setCustomReport,
  } = props;
  const {
    userData: { timezone: defaultUserTimeZone },
  } = useAuth();

  const { ref } = useOnClickOutside(handleModalClose);

  const escPress = (key: string) => {
    if (key === 'Escape') {
      handleModalClose();
    }
  };

  React.useEffect(() => {
    document.addEventListener('keyup', e => escPress(e.key));

    return () =>
      document.removeEventListener('keyup', e => escPress(e.key), true);
  }, []);

  React.useEffect(() => {
    if (recurring) {
      const deliveryTimeArray = recurring?.deliveryTime?.split(';');

      const hours: string = deliveryTimeArray[0]?.split(':')[0] || '10';
      const minutes: string = deliveryTimeArray[0]?.split(':')[1] || '00';
      setHours({ value: hours, label: hours });
      setMinutes({ value: minutes, label: minutes });
      setTimeFormat(deliveryTimeArray[1] || 'AM');

      const defaultTimezone = timezonesOptions.find(
        option => option.value === deliveryTimeArray[2]
      );
      if (defaultTimezone) {
        setTimezone(defaultTimezone);
      }
      setRecipients(recurring.recipients);
    } else {
      const defaultTimezone = timezonesOptions.find(
        option => !!option.utc.includes(defaultUserTimeZone)
      );
      if (defaultTimezone) {
        setTimezone(defaultTimezone);
      }
    }
  }, [recurring]);

  const upsertRecurring = async () => {
    setLoading(true);
    const selectedHour = `${hours.value}:${minutes.value}`;
    const deliveryTime = `${selectedHour};${timeFormat};${timezone.value};${timezone.offset}`;
    const data = {
      frequency: frequencyString,
      deliveryTime,
      recipients,
    };
    let newRecurring = {};
    if (!createNew) {
      newRecurring = await editRecurring({
        data,
        reportId,
        recurringId: recurring.recurringId,
        dispatch,
      });
    } else {
      newRecurring = await addRecurring({
        data,
        reportId,
        dispatch,
      });
    }
    if (setCustomReport) {
      setCustomReport(reportId);
    }
    setLoading(false);
    // @ts-ignore
    if (newRecurring && newRecurring.recurringId) {
      // @ts-ignore
      handleModalClose(newRecurring.recurringId);
    }
  };

  const isFormValid =
    isTimeValid(`${hours.value}:${minutes.value}`) &&
    !!timezone.value &&
    !!frequencyString &&
    recipients.length;

  return (
    <Modal style={{ position: 'fixed' }}>
      <div ref={ref} style={{ padding: '32px' }}>
        <Header>
          <HeaderTitle>
            {!createNew ? 'Edit Recurring Report' : 'New Recurring Report'}
          </HeaderTitle>
          <CloseButtonWrap title={'Close'}>
            <CloseIcon
              onClick={() => handleModalClose()}
              width={24}
              height={24}
              color={theme.palette.label}
            />
          </CloseButtonWrap>
        </Header>
        <Content>
          <div style={{ marginTop: '24px' }}>
            <Label>Recipients</Label>
            <RecipientsSelector
              onChange={recipients => setRecipients(recipients)}
              initRecipients={recurring && recurring.recipients}
              onError={email => {
                if (email) {
                  setRecipientsError('Invalid email address.');
                } else {
                  setRecipientsError('');
                }
              }}
            />
            {recipientsError && <ErrorMessage>{recipientsError}</ErrorMessage>}
          </div>
          <FrequencySelector
            margin='0px'
            frequency={frequencyString}
            setFrequency={setFrequencyString}
          />
          <div style={{ marginTop: '24px' }}>
            <Label>Preferred Delivery Time</Label>
            <div style={{ display: 'flex' }}>
              <TimeContainer>
                <Dropdown
                  icon='checkmark'
                  ariaLabel='Choose hours'
                  creatable={false}
                  defaultValue={hoursOptions[0]}
                  value={hours}
                  onChange={newValue => setHours(newValue)}
                  options={hoursOptions}
                  extendStyles={{
                    container: { width: 68 },
                    menu: { width: 95 },
                  }}
                />
                :
                <Dropdown
                  icon='checkmark'
                  ariaLabel='Choose minutes'
                  creatable={false}
                  defaultValue={minutesOptions[0]}
                  value={minutes}
                  onChange={newValue => setMinutes(newValue)}
                  options={minutesOptions}
                  extendStyles={{
                    container: { width: 68 },
                    menu: { width: 85 },
                  }}
                />
              </TimeContainer>
              <Gap>
                <ButtonSwitch
                  defaultValue={timeFormat}
                  values={timeFormats}
                  onChange={newValue => setTimeFormat(newValue)}
                />
              </Gap>
              <DropdownContainer style={{ width: '100%' }}>
                <Dropdown
                  placeholder='Timezone'
                  value={timezone}
                  creatable={false}
                  onChange={newValue => setTimezone(newValue)}
                  options={timezonesOptions}
                  className='recurringDropdown'
                />
              </DropdownContainer>
            </div>
          </div>
        </Content>
        <div>
          <PrimaryButton
            text={recurring ? 'Save' : 'Create'}
            textColor='white'
            borderRadius={5}
            fontSize='15px'
            textPadding={'8px 16px'}
            iconPadding={'8px 0px 8px 8px'}
            disabled={loading || !isFormValid}
            onClick={() => upsertRecurring()}
          />
        </div>
      </div>
    </Modal>
  );
};
